import { Injectable, computed, inject, type Signal } from "@angular/core";
import { httpResource, type HttpResourceRef } from "@angular/common/http";

import { environment } from "../../../environments/environment";
import { scopedUrl } from "../support/scoped-url";
import { ActingOrgService } from "./acting-org.service";

import type { IOrganizationFeatures } from "../models/i-organization-features";
import type { OrganizationFeature } from "../models/organization-feature";

/** Every optional page on: shown until the organization's own answer arrives. */
const ALL_ON: IOrganizationFeatures = { activity: true, documents: true };

/**
 * Which optional pages (Activity, Documents) the current organization shows.
 *
 * Read for the signed-in user's organization — or, for a Davaco employee
 * viewing as a customer, that customer's — and re-read when that changes.
 * Everything is on until the answer arrives: the common case is all on, and
 * hiding links until then would flicker them out and back for most users.
 *
 * Read-only for customers. Davaco staff change it on the organization's admin
 * page, through OrganizationsService.update, then call reload().
 *
 * A product switch, not access control. The data behind those pages is scoped
 * to the caller's customer by the API either way.
 */
@Injectable({ providedIn: "root" })
export class OrganizationFeaturesService {

  private readonly actingOrg = inject(ActingOrgService);

  public readonly resource: HttpResourceRef<IOrganizationFeatures> =
    httpResource<IOrganizationFeatures>(
      () => scopedUrl(environment.apiBaseUrl, "/portal/features", this.actingOrg.slug()),
      { defaultValue: ALL_ON }
    );

  /** The flags in force. All on while loading and if the read fails. */
  public readonly features: Signal<IOrganizationFeatures> = computed(
    (): IOrganizationFeatures => (this.resource.hasValue() ? this.resource.value() : ALL_ON)
  );

  /** True once the organization's own answer is in (or has failed). */
  public readonly settled: Signal<boolean> = computed((): boolean => !this.resource.isLoading());

  /**
   * True only when `features` is the organization's actual answer, not the
   * all-on fallback. The sidebar and guard fail open on the fallback; anything
   * that REPORTS the setting, like Settings, must wait for this.
   */
  public readonly known: Signal<boolean> = computed((): boolean => {
    const status: string = this.resource.status();
    return status === "resolved" || status === "local";
  });

  /** The read has finished and failed: the setting cannot be shown. */
  public readonly failed: Signal<boolean> = computed(
    (): boolean => this.resource.status() === "error"
  );

  public isOn(feature: OrganizationFeature): boolean {
    return this.features()[feature];
  }

  /** After a Davaco employee changes it from the admin page. */
  public reload(): void {
    this.resource.reload();
  }
}
